import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, DollarSign, Users, School, Building2, CalendarCheck2, Sparkles } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["hsl(213 73% 25%)", "hsl(146 50% 45%)", "hsl(174 60% 45%)", "hsl(38 92% 50%)", "hsl(0 73% 57%)"];

export default function OwnerDashboard() {
  const [d, setD] = useState(null);

  useEffect(() => { http.get("/ops/dashboard").then(({ data }) => setD(data)); }, []);

  if (!d) return <div className="text-muted-foreground">Loading executive dashboard…</div>;

  const growth = d.revenue.mom_growth_pct;
  const cat = Object.entries(d.revenue.by_category || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-8" data-testid="owner-executive-dashboard">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Executive dashboard</div>
        <h1 className="font-display text-3xl font-bold text-primary">Business at a Glance</h1>
        <p className="text-sm text-muted-foreground mt-1">Everything you need to run QuadScrub.</p>
      </div>

      {/* Big revenue tiles */}
      <div className="grid md:grid-cols-4 gap-4">
        <BigStat label="MRR" value={fmtUSD(d.revenue.mrr)} sub="Monthly recurring" accent="secondary" icon={<Sparkles />} />
        <BigStat label="ARR" value={fmtUSD(d.revenue.arr)} sub="Annualized run rate" accent="primary" icon={<TrendingUp />} />
        <BigStat label="This month" value={fmtUSD(d.revenue.this_month)}
                 sub={<span className={growth >= 0 ? "text-secondary" : "text-destructive"}>
                   {growth >= 0 ? <TrendingUp size={12} className="inline mr-1" /> : <TrendingDown size={12} className="inline mr-1" />}
                   {Math.abs(growth)}% vs last month
                 </span>} icon={<DollarSign />} />
        <BigStat label="Lifetime" value={fmtUSD(d.revenue.total)} sub="All-time revenue" icon={<DollarSign />} />
      </div>

      {/* Middle row */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-border/60">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-semibold text-primary">Revenue by service category</div>
              <Badge variant="outline">Lifetime</Badge>
            </div>
            {cat.length === 0 ? (
              <div className="text-sm text-muted-foreground py-16 text-center">
                No paid revenue yet. Once bookings are paid, this will populate.
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cat}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip formatter={(v) => `$${v}`} />
                    <Bar dataKey="value" fill="hsl(213 73% 25%)" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/60"><CardContent className="p-6 space-y-3">
          <div className="font-display font-semibold text-primary">Sales pipeline</div>
          <div className="text-sm">
            <Row label="Active leads" value={d.pipeline.active_leads} />
            <Row label="Won this month" value={d.pipeline.won_this_month} />
            <Row label="Active contracts" value={d.pipeline.active_contracts} />
          </div>
        </CardContent></Card>
      </div>

      {/* Footprint & ops row */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-border/60"><CardContent className="p-6">
          <div className="font-display font-semibold text-primary mb-3">Platform footprint</div>
          <div className="grid grid-cols-5 gap-4 text-center">
            <MiniStat n={d.footprint.universities} l="Universities" icon={<School size={14} />} />
            <MiniStat n={d.footprint.communities} l="Communities" icon={<Building2 size={14} />} />
            <MiniStat n={d.footprint.parents} l="Parents" icon={<Users size={14} />} />
            <MiniStat n={d.footprint.students} l="Students" icon={<Users size={14} />} />
            <MiniStat n={d.footprint.cleaners} l="Cleaners" icon={<Users size={14} />} />
          </div>
        </CardContent></Card>

        <Card className="border-border/60"><CardContent className="p-6">
          <div className="font-display font-semibold text-primary mb-3">Operations this month</div>
          <div className="grid grid-cols-2 gap-4">
            <MiniStat n={d.operations.visits_this_month} l="Total visits" icon={<CalendarCheck2 size={14} />} />
            <MiniStat n={d.operations.upcoming_visits_7d} l="Next 7 days" icon={<CalendarCheck2 size={14} />} />
            <MiniStat n={d.operations.avg_visits_per_cleaner_month} l="Visits per cleaner" />
            <MiniStat n={`${d.operations.avg_cleanliness_score}/10`} l="Avg cleanliness score" />
          </div>
        </CardContent></Card>
      </div>
    </div>
  );
}

function fmtUSD(n) { return `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`; }

function BigStat({ label, value, sub, icon, accent }) {
  const cls = accent === "secondary" ? "bg-secondary/10 text-secondary" : "bg-primary/5 text-primary";
  return (
    <Card className="border-border/60"><CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cls}`}>{icon}</div>
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-primary">{value}</div>
      <div className="text-xs text-muted-foreground uppercase tracking-wider mt-1">{label}</div>
      <div className="text-xs text-muted-foreground mt-2">{sub}</div>
    </CardContent></Card>
  );
}

function MiniStat({ n, l, icon }) {
  return (
    <div>
      {icon && <div className="text-muted-foreground mb-1 flex justify-center">{icon}</div>}
      <div className="font-display font-bold text-xl text-primary">{n}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">{l}</div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between py-2 border-b border-border/40 last:border-0"><span className="text-muted-foreground">{label}</span><span className="font-semibold text-primary">{value}</span></div>;
}
